import { retrieveCountryByCode } from '@app/feature/admin/geolocation/core/constants/geolocation.constants';
import { ContactDetailResponse, ContactResponse } from '../../domain/contact.domain';
import { Contact, ContactDetail } from '../../presentation/models/contact.model';

/**
 * Contact Mapper
 *
 * Convierte entre DTOs de dominio y modelos de presentación
 */
export class ContactMapper {
  /**
   * Convierte ContactResponse (API) a Contact (UI Model) para listas
   *
   * @param response - DTO de respuesta del API/Repository
   * @returns Modelo de contacto para UI (lista)
   */
  static toListItem(response: ContactResponse): Contact {
    const {
      contactUid,
      contactCountry,
      contactName,
      contactPaternalLastName,
      contactMaternalLastName,
    } = response;

    const fullName = `${contactName} ${contactPaternalLastName} ${contactMaternalLastName}`.trim();

    // Generar iniciales para avatar
    const initials = [contactName, contactPaternalLastName]
      .filter(Boolean)
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);

    const { countryName, countryIconPath } = retrieveCountryByCode(contactCountry);

    return {
      contactUid,
      displayName: fullName,
      countryName: countryName,
      countryCode: contactCountry,
      avatarInitials: initials,
      countryIconPath,
    };
  }

  /**
   * Convierte ContactDetailResponse (API) a ContactDetail (UI Model) para vista de detalles
   *
   * @param response - DTO de respuesta detallada del API/Repository
   * @returns Modelo de contacto detallado para UI
   */
  static toDetail(response: ContactDetailResponse): ContactDetail {
    const fullName =
      `${response.contactGivenNames} ${response.contactPaternalLastName} ${response.contactMaternalLastName}`.trim();

    // Generar iniciales para avatar
    const initials = [response.contactGivenNames, response.contactPaternalLastName]
      .filter(Boolean)
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);

    const { countryName, countryIconPath, code: countryCodes } = retrieveCountryByCode(response.contactIsoCode);

    return {
      documentType: response.contactDocumentType,
      documentNumber: response.contactDocumentNumber,
      givenNames: response.contactGivenNames,
      paternalLastName: response.contactPaternalLastName,
      maternalLastName: response.contactMaternalLastName,
      displayName: fullName,
      countryName: countryName,
      nationalityName: response.nationalityName,
      nationalityCode: response.nationalityCode|| '',
      countryIsoCode: countryCodes[0],
      countryCode: response.contactCountryCode,
      phoneCode: response.contactPhoneCode || '',
      phone: response.contactPhoneNumber || '',
      phoneNumber: response.contactPhoneCode && response.contactPhoneNumber
        ? `+${response.contactPhoneCode} ${response.contactPhoneNumber}`
        : response.contactPhone,
      bankAccountType: response.contactBankAccountType,
      bankAccountNumber: response.contactBankAccountNumber,
      stateCode: response.contactStateCode,
      cityCode: response.contactCityCode || '',
      fullAddress: response.fullContactAddress,
      reasonSending: response.reasonSending,
      avatarInitials: initials,
      countryIconPath: countryIconPath,
      email: response.contactEmail,
      bankName: response.contactBankName,
      cityLabel: response.contactCityLabel,
      contactCity: response.contactCity,
      reasonSendingCode: response.reasonSendingCode,
      birthDate: response.contactBirthDate,
      postalCode: response.contactPostalCode,
      bankCode: response.contactBankCode,
      department: response.contactDepartment,
      municipality: response.contactMunicipality,
      canUpdateBankAccount: response.canUpdateBankAccountType || false,
    };
  }

  /**
   * Convierte array de ContactResponse a array de Contact
   *
   * @param responses - Array de DTOs de respuesta
   * @returns Array de modelos de contacto para listas
   */
  static toListItems(responses: ContactResponse[]): Contact[] {
    return responses.map(response => this.toListItem(response));
  }
}
